import { createLocalRoom, DEFAULT_TEAMS, ALLIANCE_LETTERS } from '../network/room';
import { GameSession } from '../network/gameSession';
import { NetworkManager } from '../network/networkManager';
import type { TeamConfig } from '../logic/team';
import { type GameSettings, createGameSettings } from '../core/gameSettings';
import { MODE_PRESETS, cloneModeConfig } from '../core/gameModes';
import { generateRandomSeed, SeededRNG } from '../core/rng';
import { assignRandomTanks } from '../core/tankTypes';
import { analytics } from '../utils/analytics';
import QRCode from 'qrcode';
import { LOBBY_STYLES } from './styles/lobbyStyles';
import { installDesignSystem } from './styles/designSystem';
import { LobbyRenderer } from './lobby/LobbyRenderer';
import { soundManager } from '../audio/soundManager';
import { gameLogger, type LogCategory } from '../core/gameLogger';

export interface LobbyCallbacks {
  onStartGame: (
    session: GameSession,
    teams: TeamConfig[],
    networkManager: NetworkManager | null,
    isHost: boolean,
    terrainSeed?: number,
    settings?: GameSettings,
  ) => void;
}

export class LobbyScreen {
  private container: HTMLElement;
  private session: GameSession | null = null;
  private networkManager: NetworkManager | null = null;
  private callbacks: LobbyCallbacks;
  private roomCode: string = '';
  private gameSettings: GameSettings = createGameSettings();
  private gameSeed: number = generateRandomSeed();
  /** Host toggle: shuffle teams into balanced alliances when the match starts. */
  private randomizeAlliancesOnStart = true;

  constructor(container: HTMLElement, callbacks: LobbyCallbacks) {
    this.container = container;
    this.callbacks = callbacks;
  }

  show(): void {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    this.renderMainMenu();
  }

  showLobby(): void {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    if (this.session && this.networkManager) {
      this.renderHostLobby();
    } else if (this.session) {
      this.renderLocalLobby();
    } else {
      this.renderMainMenu();
    }
  }

  showAndJoinRoom(roomCode: string): void {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    const playerName = localStorage.getItem('worms_player_name') || 'Player 2';
    this.joinNetworkGame(roomCode, playerName);
  }

  private getRoomUrl(): string {
    const base = window.location.origin + window.location.pathname;
    return `${base}#room/${this.roomCode}`;
  }

  private async generateQRCodeDataUrl(text: string): Promise<string> {
    try {
      return await QRCode.toDataURL(text, {
        width: 150,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });
    } catch (err) {
      console.error('QR code generation failed:', err);
      return '';
    }
  }

  private renderMainMenu(): void {
    const savedName = localStorage.getItem('worms_player_name') || 'Player 1';
    this.container.innerHTML = LobbyRenderer.renderMainMenu(
      savedName,
      __GIT_HASH__,
      __BUILD_TIME__,
    );
    this.addStyles();

    document.getElementById('btn-local-game')?.addEventListener('click', () => {
      const playerName = this.getPlayerName();
      this.createLocalGame(playerName);
    });

    document.getElementById('btn-host-game')?.addEventListener('click', () => {
      const playerName = this.getPlayerName();
      this.hostNetworkGame(playerName);
    });

    document.getElementById('btn-join-game')?.addEventListener('click', () => {
      this.showJoinScreen();
    });

    document.getElementById('player-name')?.addEventListener('input', (e) => {
      const value = (e.target as HTMLInputElement).value.trim();
      if (value) {
        localStorage.setItem('worms_player_name', value);
      }
    });

    document.getElementById('btn-settings')?.addEventListener('click', () => {
      this.showTitleSettings();
    });
  }

  private showTitleSettings(): void {
    const existingOverlay = document.getElementById('title-settings-overlay');
    if (existingOverlay) return;

    const logSettings = gameLogger.getSettings();
    const masterVol = Math.round(soundManager.getMasterVolume() * 100);
    const voiceVol = Math.round(soundManager.getVoiceVolume() * 100);
    const soundEnabled = soundManager.isEnabled();
    const categories: LogCategory[] = ['Turn', 'Weapon', 'Damage', 'Network', 'Entity', 'Game'];

    const overlay = document.createElement('div');
    overlay.id = 'title-settings-overlay';
    overlay.className = 'title-settings-overlay';
    overlay.innerHTML = `
      <div class="title-settings-panel">
        <h2>Settings</h2>
        <div class="settings-group">
          <div class="setting-slider">
            <div class="setting-slider-header">
              <span>Master Volume</span>
              <span id="master-vol-label">${masterVol}%</span>
            </div>
            <input type="range" id="setting-master-vol" min="0" max="100" value="${masterVol}" />
          </div>
          <div class="setting-slider">
            <div class="setting-slider-header">
              <span>Voice Volume</span>
              <span id="voice-vol-label">${voiceVol}%</span>
            </div>
            <input type="range" id="setting-voice-vol" min="0" max="100" value="${voiceVol}" />
          </div>
          <label class="setting-toggle">
            <input type="checkbox" id="setting-sound-enabled" ${soundEnabled ? 'checked' : ''} />
            <span>Sound Enabled</span>
          </label>
          <div class="setting-divider">--- Debug Logging ---</div>
          <label class="setting-toggle">
            <input type="checkbox" id="setting-logs-enabled" ${logSettings.enabled ? 'checked' : ''} />
            <span>Enable Logs</span>
          </label>
          <div class="log-categories">
            ${categories
              .map(
                (cat) => `
              <label class="setting-toggle">
                <input type="checkbox" class="log-cat-toggle" data-category="${cat}" ${logSettings.categories[cat] ? 'checked' : ''} />
                <span>${cat}</span>
              </label>
            `,
              )
              .join('')}
          </div>
        </div>
        <div class="settings-actions">
          <button id="btn-close-settings" class="lobby-btn primary">Apply &amp; Close</button>
        </div>
      </div>
    `;

    this.container.appendChild(overlay);

    document.getElementById('setting-master-vol')?.addEventListener('input', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      soundManager.setMasterVolume(value / 100);
      const label = document.getElementById('master-vol-label');
      if (label) label.textContent = `${value}%`;
    });

    document.getElementById('setting-voice-vol')?.addEventListener('input', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      soundManager.setVoiceVolume(value / 100);
      const label = document.getElementById('voice-vol-label');
      if (label) label.textContent = `${value}%`;
    });

    document.getElementById('setting-sound-enabled')?.addEventListener('change', (e) => {
      soundManager.setEnabled((e.target as HTMLInputElement).checked);
    });

    document.getElementById('setting-logs-enabled')?.addEventListener('change', (e) => {
      if ((e.target as HTMLInputElement).checked) {
        gameLogger.enable();
      } else {
        gameLogger.disable();
      }
    });

    document.querySelectorAll('.log-cat-toggle').forEach((toggle) => {
      toggle.addEventListener('change', (e) => {
        const input = e.target as HTMLInputElement;
        const category = input.dataset.category as LogCategory;
        if (input.checked) {
          gameLogger.enableCategory(category);
        } else {
          gameLogger.disableCategory(category);
        }
      });
    });

    document.getElementById('btn-close-settings')?.addEventListener('click', () => {
      overlay.remove();
    });
  }

  private getPlayerName(): string {
    const nameInput = document.getElementById('player-name') as HTMLInputElement;
    const name = nameInput?.value.trim() || 'Player 1';
    localStorage.setItem('worms_player_name', name);
    return name;
  }

  private createLocalGame(playerName: string): void {
    const { room, host } = createLocalRoom(playerName);
    this.session = new GameSession(room, host);
    this.renderLocalLobby();
  }

  private async hostNetworkGame(playerName: string): Promise<void> {
    this.networkManager = new NetworkManager(playerName);

    this.showLoadingScreen('Creating room...');

    try {
      this.roomCode = await this.networkManager.createRoom(`${playerName}'s Game`, DEFAULT_TEAMS);
      history.replaceState(null, '', `#room/${this.roomCode}`);
      analytics.roomCreated();

      const room = this.networkManager.getRoom()!;
      this.session = new GameSession(room, this.networkManager.getLocalPlayer());

      this.networkManager.setEventHandlers({
        onPlayerJoined: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderHostLobby();
          }
        },
        onPlayerLeft: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderHostLobby();
          }
        },
        onPlayerReconnected: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderHostLobby();
          }
        },
        onTeamClaimed: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderHostLobby();
          }
        },
        onTeamReleased: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderHostLobby();
          }
        },
        onStateSync: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderHostLobby();
          }
        },
        onGameStart: (terrainSeed?: number, teams?: TeamConfig[], settings?: GameSettings) => {
          if (this.session) {
            const activeTeams = teams || this.session.getActiveTeamConfigs();
            this.callbacks.onStartGame(
              this.session,
              activeTeams,
              this.networkManager,
              true,
              terrainSeed,
              settings ?? this.gameSettings,
            );
          }
        },
        onConnectionStateChanged: () => this.updateConnectionStatus(),
      });

      this.renderHostLobby();
    } catch (e) {
      console.error('Failed to create room:', e);
      alert(`Failed to create room: ${e instanceof Error ? e.message : 'Unknown error'}`);
      this.networkManager?.disconnect();
      this.networkManager = null;
      this.renderMainMenu();
    }
  }

  private showJoinScreen(): void {
    const savedName = localStorage.getItem('worms_player_name') || 'Player 2';

    this.container.innerHTML = `
      <div class="lobby-container">
        <h1 class="lobby-title">Join Game</h1>
        <div class="join-section">
          <label for="room-code">Enter Room Code:</label>
          <div class="room-code-input-wrapper">
            <input type="text" id="room-code" placeholder="123456" maxlength="12"
              inputmode="numeric" pattern="[0-9]*" autocomplete="off" />
          </div>
          <button id="btn-connect" class="lobby-btn">Join</button>
        </div>
        <div class="lobby-input-group">
          <label for="player-name-join">Your Name:</label>
          <input type="text" id="player-name-join" value="${savedName}" maxlength="20" />
        </div>
        <div class="lobby-actions">
          <button id="btn-back" class="lobby-btn secondary">Back</button>
        </div>
      </div>
    `;

    this.addStyles();

    document.getElementById('btn-back')?.addEventListener('click', () => {
      this.renderMainMenu();
    });

    const joinGame = async () => {
      const codeInput = document.getElementById('room-code') as HTMLInputElement;
      const nameInput = document.getElementById('player-name-join') as HTMLInputElement;
      // Codes are numeric; full legacy codes (WORMS-XXXXXX) still paste fine.
      const roomCode = codeInput.value.trim().toUpperCase();
      const playerName = nameInput.value.trim() || 'Player 2';
      localStorage.setItem('worms_player_name', playerName);

      if (!roomCode) {
        alert('Please enter a room code');
        return;
      }

      await this.joinNetworkGame(roomCode, playerName);
    };

    document.getElementById('btn-connect')?.addEventListener('click', joinGame);

    document.getElementById('room-code')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        joinGame();
      }
    });

    document.getElementById('player-name-join')?.addEventListener('input', (e) => {
      const value = (e.target as HTMLInputElement).value.trim();
      if (value) {
        localStorage.setItem('worms_player_name', value);
      }
    });
  }

  private async joinNetworkGame(roomCode: string, playerName: string): Promise<void> {
    this.networkManager = new NetworkManager(playerName);
    this.roomCode = roomCode;

    this.showLoadingScreen('Connecting...');

    try {
      await this.networkManager.joinRoom(roomCode);
      history.replaceState(null, '', `#room/${this.roomCode}`);
      analytics.roomJoined();

      const room = this.networkManager.getRoom()!;
      this.session = new GameSession(room, this.networkManager.getLocalPlayer());

      this.networkManager.setEventHandlers({
        onStateSync: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderClientLobby();
          }
        },
        onTeamClaimed: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderClientLobby();
          }
        },
        onTeamReleased: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderClientLobby();
          }
        },
        onGameStart: (terrainSeed?: number, teams?: TeamConfig[], settings?: GameSettings) => {
          if (this.session) {
            const activeTeams = teams || this.session.getActiveTeamConfigs();
            this.callbacks.onStartGame(
              this.session,
              activeTeams,
              this.networkManager,
              false,
              terrainSeed,
              settings,
            );
          }
        },
        onReconnecting: (attempt, maxAttempts) => this.showReconnectingStatus(attempt, maxAttempts),
        onReconnected: () => {
          if (this.session?.getRoom().state === 'lobby') {
            this.renderClientLobby();
          }
        },
        onReconnectFailed: (reason) => this.handleReconnectFailed(reason),
        onConnectionStateChanged: () => this.updateConnectionStatus(),
      });

      if (room.state === 'playing') {
        this.showLoadingScreen('Game in progress...\nReconnecting...');
      } else {
        this.renderClientLobby();
      }
    } catch (e) {
      console.error('Failed to join room:', e);
      alert(`Failed to join: ${e instanceof Error ? e.message : 'Unknown error'}`);
      this.networkManager?.disconnect();
      this.networkManager = null;
      this.showJoinScreen();
    }
  }

  private showLoadingScreen(message: string): void {
    this.container.innerHTML = LobbyRenderer.renderLoadingScreen(message);
    this.addStyles();
  }

  private async renderHostLobby(): Promise<void> {
    if (!this.session || !this.networkManager) return;

    const room = this.session.getRoom();
    const localPlayer = this.session.getLocalPlayer();
    const teamSlots = room.getTeamSlots();

    const teamsHtml = LobbyRenderer.renderTeamSlots(
      teamSlots,
      localPlayer.id,
      room,
      this.randomizeAlliancesOnStart,
      this.gameSettings.mode.tankSelection ?? false,
    );
    const playersHtml = LobbyRenderer.renderPlayerList(room, localPlayer.id);
    const settingsHtml = LobbyRenderer.renderGameSettings(
      this.gameSettings,
      this.gameSeed,
      true,
      room.maxTeamsPerPlayer,
    );
    const canStart = this.session.canStart();
    const startHint = LobbyRenderer.getStartHint(room, canStart);
    const connInfo = this.networkManager.getConnectionInfo();
    const peerCount = this.networkManager.getPeerCount();
    const connHtml = connInfo ? LobbyRenderer.renderConnectionBadge(connInfo, peerCount) : '';
    const roomUrl = this.getRoomUrl();
    const qrCodeDataUrl = await this.generateQRCodeDataUrl(roomUrl);

    // #app (this.container) is the actual scroll container, not
    // .lobby-container. Preserve its scroll across the innerHTML re-render so
    // server-driven lobby refreshes don't jump back to the top.
    const scrollTop = this.container.scrollTop || 0;

    this.container.innerHTML = LobbyRenderer.renderHostLobby({
      roomCode: this.roomCode,
      teamsHtml,
      playersHtml,
      settingsHtml,
      canStart,
      startHint,
      connHtml,
      qrCodeDataUrl,
      playerCount: room.playerCount,
      maxPlayers: room.maxPlayers,
    });

    this.addStyles();

    this.attachLobbyEvents(true);
    this.attachSettingsEvents();

    this.container.scrollTop = scrollTop;

    document.getElementById('btn-copy-code')?.addEventListener('click', () => {
      navigator.clipboard.writeText(roomUrl);
      const btn = document.getElementById('btn-copy-code');
      if (btn) {
        btn.textContent = 'Copied!';
        setTimeout(() => {
          btn.textContent = 'Copy Link';
        }, 2000);
      }
    });
  }

  private renderClientLobby(): void {
    if (!this.session || !this.networkManager) return;

    const room = this.session.getRoom();
    const localPlayer = this.session.getLocalPlayer();
    const teamSlots = room.getTeamSlots();

    const teamsHtml = LobbyRenderer.renderTeamSlots(
      teamSlots,
      localPlayer.id,
      room,
      this.randomizeAlliancesOnStart,
      this.gameSettings.mode.tankSelection ?? false,
    );
    const playersHtml = LobbyRenderer.renderPlayerList(room, localPlayer.id);
    const connInfo = this.networkManager.getConnectionInfo();
    const peerCount = this.networkManager.getPeerCount();
    const connHtml = connInfo ? LobbyRenderer.renderConnectionBadge(connInfo, peerCount) : '';

    // Preserve #app's scroll across the re-render so server-driven refreshes
    // (a peer joins, the host edits a setting, etc.) don't jump to the top.
    const scrollTop = this.container.scrollTop || 0;

    this.container.innerHTML = LobbyRenderer.renderClientLobby({
      roomName: room.name,
      teamsHtml,
      playersHtml,
      connHtml,
      playerCount: room.playerCount,
      maxPlayers: room.maxPlayers,
    });

    this.addStyles();
    this.attachLobbyEvents(false);

    this.container.scrollTop = scrollTop;
  }

  private renderLocalLobby(): void {
    if (!this.session) return;

    const room = this.session.getRoom();
    const localPlayer = this.session.getLocalPlayer();
    if (!room || !localPlayer) return;

    const teamSlots = room.getTeamSlots();

    const teamsHtml = LobbyRenderer.renderTeamSlots(
      teamSlots,
      localPlayer.id,
      room,
      this.randomizeAlliancesOnStart,
      this.gameSettings.mode.tankSelection ?? false,
    );
    const playersHtml = LobbyRenderer.renderPlayerList(room, localPlayer.id);
    const settingsHtml = LobbyRenderer.renderGameSettings(
      this.gameSettings,
      this.gameSeed,
      true,
      room.maxTeamsPerPlayer,
    );
    const canStart = this.session.canStart();
    const startHint = LobbyRenderer.getStartHint(room, canStart);

    const scrollTop = this.container.scrollTop || 0;

    this.container.innerHTML = LobbyRenderer.renderLocalLobby({
      roomName: room.name,
      teamsHtml,
      playersHtml,
      settingsHtml,
      canStart,
      startHint,
      playerCount: room.playerCount,
      maxPlayers: room.maxPlayers,
    });

    this.addStyles();

    this.attachLocalLobbyEvents();

    this.container.scrollTop = scrollTop;
  }

  private attachSettingsEvents(): void {
    document.getElementById('setting-game-mode')?.addEventListener('change', (e) => {
      const id = (e.target as HTMLSelectElement).value as keyof typeof MODE_PRESETS;
      const preset = MODE_PRESETS[id];
      if (preset) {
        // Presets are defaults: load a fresh copy, then every field stays
        // editable in the lobby like any other setting.
        this.gameSettings.mode = cloneModeConfig(preset);
        if (this.networkManager) {
          this.renderHostLobby();
        } else {
          this.renderLocalLobby();
        }
      }
    });

    document.getElementById('setting-barrels')?.addEventListener('change', (e) => {
      this.gameSettings.hazards.oilBarrels = (e.target as HTMLInputElement).checked;
    });

    document.getElementById('setting-mines')?.addEventListener('change', (e) => {
      this.gameSettings.hazards.prePlacedMines = (e.target as HTMLInputElement).checked;
    });

    document.getElementById('setting-crates')?.addEventListener('change', (e) => {
      this.gameSettings.worm.crateDropChance = (e.target as HTMLInputElement).checked ? 0.3 : 0;
    });

    document.getElementById('setting-girders')?.addEventListener('change', (e) => {
      this.gameSettings.terrain.girders = (e.target as HTMLInputElement).checked;
    });

    document.getElementById('setting-graves')?.addEventListener('change', (e) => {
      this.gameSettings.worm.gravesOnDeath = (e.target as HTMLInputElement).checked;
    });

    document.getElementById('setting-infinite-ammo')?.addEventListener('change', (e) => {
      this.gameSettings.worm.infiniteAmmo = (e.target as HTMLInputElement).checked;
    });

    document.getElementById('setting-random-tank')?.addEventListener('change', (e) => {
      this.gameSettings.mode.randomTank = (e.target as HTMLInputElement).checked;
    });

    document.getElementById('setting-worms-per-team')?.addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLSelectElement).value, 10);
      if (value >= 1 && value <= 4) {
        this.gameSettings.worm.wormsPerTeam = value;
      }
    });

    document.getElementById('setting-turn-time')?.addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      if (!isNaN(value) && value >= 5 && value <= 180) {
        this.gameSettings.turn.turnTime = value;
      }
    });

    document.getElementById('setting-worm-health')?.addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      if (!isNaN(value) && value >= 1 && value <= 1000) {
        this.gameSettings.worm.initialHealth = value;
      }
    });

    document.getElementById('setting-movement-budget')?.addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      if (!isNaN(value) && value >= 0 && value <= 2000) {
        this.gameSettings.mode.worm.movementBudgetPx = value > 0 ? value : null;
      }
    });

    document.querySelectorAll('.whitelist-toggle').forEach((toggle) => {
      toggle.addEventListener('change', (e) => {
        const input = e.target as HTMLInputElement;
        const weaponId = input.dataset.weaponId;
        const whitelist = this.gameSettings.mode.items.whitelist;
        if (!weaponId || !whitelist) return;
        if (input.checked && !whitelist.includes(weaponId)) {
          whitelist.push(weaponId);
        } else if (!input.checked) {
          this.gameSettings.mode.items.whitelist = whitelist.filter((w) => w !== weaponId);
        }
      });
    });

    document.getElementById('setting-weather-interval')?.addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      if (!isNaN(value) && value >= 1 && value <= 20) {
        this.gameSettings.mode.weather.intervalTurns = value;
      }
    });

    document.querySelectorAll('.match-item-count').forEach((input) => {
      input.addEventListener('change', (e) => {
        const el = e.target as HTMLInputElement;
        const itemId = el.dataset.itemId;
        const value = parseInt(el.value, 10);
        if (!itemId || isNaN(value)) return;
        const clamped = Math.max(0, Math.min(9, value));
        el.value = String(clamped);
        const matchItems = this.gameSettings.mode.items.matchItems;
        if (clamped > 0) {
          matchItems[itemId] = clamped;
        } else {
          delete matchItems[itemId];
        }
      });
    });

    document.getElementById('setting-map-style')?.addEventListener('change', (e) => {
      const style = (e.target as HTMLSelectElement).value as
        | 'open'
        | 'cavern'
        | 'random'
        | 'island'
        | 'bridges'
        | 'towers'
        | 'fortress'
        | 'tunnels'
        | 'platforms';
      this.gameSettings.terrain.style = style;
      const isClassic = ['open', 'cavern'].includes(style);
      const isRandomHills = style === 'random';
      const themeContainer = document.getElementById('theme-container');
      const complexityContainer = document.getElementById('complexity-container');
      const floatingContainer = document.getElementById('floating-islands-container');
      const bridgesContainer = document.getElementById('bridges-container');
      if (themeContainer) themeContainer.style.display = isClassic ? 'none' : '';
      if (complexityContainer) complexityContainer.style.display = isClassic ? 'none' : '';
      if (floatingContainer) floatingContainer.style.display = isRandomHills ? '' : 'none';
      if (bridgesContainer) bridgesContainer.style.display = isRandomHills ? '' : 'none';
    });

    document.getElementById('setting-theme')?.addEventListener('change', (e) => {
      this.gameSettings.terrain.theme = (e.target as HTMLSelectElement).value as
        | 'dirt'
        | 'snow'
        | 'hell'
        | 'jungle'
        | 'desert'
        | 'alien'
        | 'random';
    });

    document.getElementById('setting-complexity')?.addEventListener('change', (e) => {
      this.gameSettings.terrain.complexity = (e.target as HTMLSelectElement).value as
        | 'simple'
        | 'normal'
        | 'complex';
    });

    document.getElementById('setting-floating-islands')?.addEventListener('change', (e) => {
      this.gameSettings.terrain.floatingIslands = (e.target as HTMLInputElement).checked;
    });

    document.getElementById('setting-bridges')?.addEventListener('change', (e) => {
      this.gameSettings.terrain.bridges = (e.target as HTMLInputElement).checked;
    });

    document.getElementById('setting-sudden-death')?.addEventListener('change', (e) => {
      this.gameSettings.suddenDeath.enabled = (e.target as HTMLInputElement).checked;
      const turnsContainer = document.getElementById('sudden-death-turns-container');
      if (turnsContainer) {
        turnsContainer.style.display = this.gameSettings.suddenDeath.enabled ? '' : 'none';
      }
    });

    document.getElementById('setting-sudden-death-turns')?.addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      if (!isNaN(value) && value >= 5 && value <= 100) {
        this.gameSettings.suddenDeath.turnsBeforeStart = value;
      }
    });

    document.getElementById('setting-seed')?.addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      if (!isNaN(value)) {
        this.gameSeed = value >>> 0;
        (e.target as HTMLInputElement).value = String(this.gameSeed);
      }
    });

    document.getElementById('btn-random-seed')?.addEventListener('click', () => {
      this.gameSeed = generateRandomSeed();
      const input = document.getElementById('setting-seed') as HTMLInputElement;
      if (input) input.value = String(this.gameSeed);
    });

    document.getElementById('max-teams-per-player')?.addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10);
      if (value >= 1 && value <= 8) {
        const room = this.session?.getRoom();
        if (room) {
          room.setMaxTeamsPerPlayer(value);
          if (this.networkManager) {
            this.networkManager.broadcastMaxTeamsPerPlayer(value);
            this.renderHostLobby();
          } else {
            this.renderLocalLobby();
          }
        }
      }
    });
  }

  private attachLobbyEvents(isHost: boolean): void {
    document.getElementById('btn-back')?.addEventListener('click', () => {
      this.networkManager?.disconnect();
      this.networkManager = null;
      this.session = null;
      this.gameSettings = createGameSettings();
      history.replaceState(null, '', window.location.pathname);
      this.renderMainMenu();
    });

    if (isHost) {
      document.getElementById('btn-start')?.addEventListener('click', () => {
        // Optionally shuffle the teams into balanced alliances right before the
        // match starts (server applies the shuffle before it reads the start).
        if (this.randomizeAlliancesOnStart) this.networkManager?.randomizeAlliances();
        // The game starts when the server broadcasts 'game-start' (handled
        // via onGameStart for host and clients alike).
        this.networkManager?.startGame(this.gameSeed, this.gameSettings);
      });
    }

    document.querySelectorAll('.team-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const slot = target.closest('.team-slot') as HTMLElement;
        const teamId = slot?.dataset.teamId;
        if (!teamId || !this.networkManager) return;

        if (target.classList.contains('release')) {
          this.networkManager.releaseTeam(teamId);
        } else {
          this.networkManager.claimTeam(teamId);
        }

        if (isHost) {
          this.renderHostLobby();
        }
      });
    });

    document.querySelectorAll('.alliance-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const teamId = (e.target as HTMLElement).dataset.teamId;
        const next = this.nextAllianceFor(teamId);
        if (!teamId || !next || !this.networkManager) return;
        this.networkManager.setTeamAlliance(teamId, next);
        if (isHost) {
          this.renderHostLobby();
        } else {
          this.renderClientLobby();
        }
      });
    });

    document.querySelectorAll('.tank-type-select').forEach((sel) => {
      sel.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const teamId = target.dataset.teamId;
        if (!teamId || !this.networkManager) return;
        this.networkManager.setTankType(teamId, target.value);
        if (isHost) {
          this.renderHostLobby();
        } else {
          this.renderClientLobby();
        }
      });
    });

    (
      document.querySelector('.alliance-count-select') as HTMLSelectElement | null
    )?.addEventListener('change', (e) => {
      const count = parseInt((e.target as HTMLSelectElement).value, 10);
      this.networkManager?.setAllianceCount(count);
      if (isHost) this.renderHostLobby();
    });
    (
      document.querySelector('.randomize-on-start-check') as HTMLInputElement | null
    )?.addEventListener('change', (e) => {
      this.randomizeAlliancesOnStart = (e.target as HTMLInputElement).checked;
    });

    document.querySelectorAll('.kick-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const playerId = target.dataset.playerId;
        if (!playerId || !this.networkManager) return;

        const player = this.session?.getRoom().getPlayer(playerId);
        const playerName = player?.name || 'this player';

        if (confirm(`Kick ${playerName} from the lobby?`)) {
          this.networkManager.kickPlayer(playerId);
          if (isHost) {
            this.renderHostLobby();
          }
        }
      });
    });

    document.querySelectorAll('.spectator-checkbox').forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        const isSpectator = (e.target as HTMLInputElement).checked;
        if (this.networkManager) {
          this.networkManager.toggleSpectator(isSpectator);
          if (isHost) {
            this.renderHostLobby();
          } else {
            this.renderClientLobby();
          }
        }
      });
    });

    this.container.addEventListener(
      'blur',
      (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('team-name-input')) {
          const input = target as HTMLInputElement;
          const teamId = input.dataset.teamId;
          const newName = input.value.trim();

          if (teamId && newName && newName.length > 0 && newName.length <= 20) {
            const room = this.session?.getRoom();
            if (room && room.updateTeamName(teamId, newName)) {
              if (this.networkManager) {
                this.networkManager.broadcastTeamNameChanged(teamId, newName);
              }
              if (isHost) {
                this.renderHostLobby();
              } else {
                this.renderClientLobby();
              }
            } else {
              const slot = room?.getTeamSlots().find((s) => s.config.id === teamId);
              if (slot) input.value = slot.config.name;
            }
          }
        }
      },
      true,
    );
  }

  private attachLocalLobbyEvents(): void {
    document.getElementById('btn-back')?.addEventListener('click', () => {
      this.session?.destroy();
      this.session = null;
      this.gameSettings = createGameSettings();
      this.renderMainMenu();
    });

    document.getElementById('btn-start')?.addEventListener('click', () => {
      // Shuffle teams into balanced alliances right before starting, if opted in.
      if (this.randomizeAlliancesOnStart) this.session?.getRoom().randomizeAlliances();
      if (this.session?.startGame()) {
        let teams = this.session.getActiveTeamConfigs();
        // Local play has no server, so assign random tanks here (seeded by the
        // match seed) before building the game.
        if (this.gameSettings.mode.randomTank) {
          const tankRng = new SeededRNG(this.gameSeed);
          teams = assignRandomTanks(teams, () => tankRng.next());
        }
        this.callbacks.onStartGame(
          this.session,
          teams,
          null,
          true,
          this.gameSeed,
          this.gameSettings,
        );
      }
    });

    document.querySelectorAll('.team-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const slot = target.closest('.team-slot') as HTMLElement;
        const teamId = slot?.dataset.teamId;
        if (!teamId || !this.session) return;

        if (target.classList.contains('release')) {
          this.session.releaseTeam(teamId);
        } else {
          this.session.claimTeam(teamId);
        }
        this.renderLocalLobby();
      });
    });

    document.querySelectorAll('.alliance-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const teamId = (e.target as HTMLElement).dataset.teamId;
        const next = this.nextAllianceFor(teamId);
        const room = this.session?.getRoom();
        if (!teamId || !next || !room) return;
        room.setTeamAlliance(teamId, next);
        this.renderLocalLobby();
      });
    });

    document.querySelectorAll('.tank-type-select').forEach((sel) => {
      sel.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const teamId = target.dataset.teamId;
        const room = this.session?.getRoom();
        if (!teamId || !room) return;
        room.setTankType(teamId, target.value);
        this.renderLocalLobby();
      });
    });

    (
      document.querySelector('.alliance-count-select') as HTMLSelectElement | null
    )?.addEventListener('change', (e) => {
      const count = parseInt((e.target as HTMLSelectElement).value, 10);
      this.session?.getRoom().setAllianceCount(count);
      this.renderLocalLobby();
    });
    (
      document.querySelector('.randomize-on-start-check') as HTMLInputElement | null
    )?.addEventListener('change', (e) => {
      this.randomizeAlliancesOnStart = (e.target as HTMLInputElement).checked;
    });

    document.querySelectorAll('.spectator-checkbox').forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        const isSpectator = (e.target as HTMLInputElement).checked;
        const room = this.session?.getRoom();
        const localPlayer = this.session?.getLocalPlayer();
        if (room && localPlayer) {
          room.setPlayerSpectator(localPlayer.id, isSpectator);
          this.renderLocalLobby();
        }
      });
    });

    this.container.addEventListener(
      'blur',
      (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('team-name-input')) {
          const input = target as HTMLInputElement;
          const teamId = input.dataset.teamId;
          const newName = input.value.trim();

          if (teamId && newName && newName.length > 0 && newName.length <= 20) {
            const room = this.session?.getRoom();
            if (room && room.updateTeamName(teamId, newName)) {
              this.renderLocalLobby();
            } else {
              const slot = room?.getTeamSlots().find((s) => s.config.id === teamId);
              if (slot) input.value = slot.config.name;
            }
          }
        }
      },
      true,
    );

    this.attachSettingsEvents();
  }

  /** Next alliance letter in the cycle for a claimed team (null if not cyclable). */
  private nextAllianceFor(teamId: string | undefined): string | null {
    if (!teamId) return null;
    const room = this.session?.getRoom();
    const slot = room?.getTeamSlots().find((s) => s.config.id === teamId);
    if (!room || !slot || slot.ownerId === null) return null;
    // Cycle only within the active alliances (columns), not all 8 letters.
    const letters = ALLIANCE_LETTERS.slice(0, room.allianceCount);
    const current = slot.config.allianceId ?? letters[0];
    const idx = letters.indexOf(current as (typeof letters)[number]);
    return letters[(idx + 1) % letters.length];
  }

  private showReconnectingStatus(attempt: number, maxAttempts: number): void {
    const existingOverlay = document.getElementById('reconnect-overlay');
    if (existingOverlay) {
      const statusText = existingOverlay.querySelector('.reconnect-status');
      if (statusText) {
        statusText.textContent = `Reconnecting... (${attempt}/${maxAttempts})`;
      }
      return;
    }

    const overlay = document.createElement('div');
    overlay.id = 'reconnect-overlay';
    overlay.innerHTML = `
      <div class="reconnect-modal">
        <div class="reconnect-spinner"></div>
        <div class="reconnect-status">Reconnecting... (${attempt}/${maxAttempts})</div>
        <div class="reconnect-hint">Please wait while we restore your connection</div>
        <button id="btn-cancel-reconnect" class="lobby-btn secondary">Cancel</button>
      </div>
    `;
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    `;
    this.container.appendChild(overlay);

    document.getElementById('btn-cancel-reconnect')?.addEventListener('click', () => {
      this.networkManager?.disconnect();
      this.networkManager = null;
      this.session = null;
      overlay.remove();
      this.renderMainMenu();
    });
  }

  private handleReconnectFailed(reason: string): void {
    const overlay = document.getElementById('reconnect-overlay');
    if (overlay) overlay.remove();

    alert(`Failed to reconnect: ${reason}`);
    this.networkManager?.disconnect();
    this.networkManager = null;
    this.session = null;
    this.renderMainMenu();
  }

  private updateConnectionStatus(): void {
    if (!this.networkManager) return;

    const state = this.networkManager.getConnectionState();
    const statusEl = document.getElementById('connection-status');

    if (statusEl) {
      let statusClass = '';
      let statusText = '';

      switch (state) {
        case 'connected':
          statusClass = 'status-connected';
          statusText = 'Connected';
          break;
        case 'reconnecting':
          statusClass = 'status-reconnecting';
          statusText = 'Reconnecting...';
          break;
        case 'disconnected':
          statusClass = 'status-disconnected';
          statusText = 'Disconnected';
          break;
      }

      statusEl.className = `connection-status ${statusClass}`;
      statusEl.textContent = statusText;
    }

    if (state === 'connected') {
      const overlay = document.getElementById('reconnect-overlay');
      if (overlay) overlay.remove();
    }
  }

  private addStyles(): void {
    installDesignSystem();
    if (document.getElementById('lobby-styles')) return;

    const style = document.createElement('style');
    style.id = 'lobby-styles';
    style.textContent = LOBBY_STYLES;
    document.head.appendChild(style);
  }

  hide(): void {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    this.container.innerHTML = '';
  }

  destroy(): void {
    this.session?.destroy();
    this.networkManager?.disconnect();
    this.hide();
  }
}
